import React from 'react'

import { Theme } from '../../../types/shared'
import { ButtonColorPicker, DialogBody, DialogFooter, DialogHeader, InnerState } from '../../components'
import ChartLayoutModel from '../../models/chart_layout'
import { IChartThemeColors, chartDarkTheme, chartLightTheme } from '../../models/constants'
import { getChartLayoutSettings } from '../../models/settings'
import { useForceUpdate } from '../../utils/force-update'
import { useChartLayoutGlobalModel } from './chart-layout-model'
import { ChartTabComponentProps, SettingsTab } from './constants'
import { useSaveSettings } from './save-settings'
import { SettingsActionFooter } from './settings-action-footer'

const editableColorGroups: Array<{
  title: string
  colors: Array<{
    label: string
    colorKeys: Array<keyof IChartThemeColors>
    canSelectAlpha: boolean
  }>
}> = [
  {
    title: 'Candle, Heikin Ashi',
    colors: [
      { label: 'Body', colorKeys: ['trendUp', 'trendDown'], canSelectAlpha: true },
      { label: 'Borders', colorKeys: ['borderUp', 'borderDown'], canSelectAlpha: true },
      { label: 'Wick', colorKeys: ['wickUp', 'wickDown'], canSelectAlpha: true },
    ],
  },
  {
    title: 'Hollow Candle, OHLC',
    colors: [{ label: 'Body', colorKeys: ['ohlcTrendUp', 'ohlcTrendDown'], canSelectAlpha: false }],
  },
  {
    title: 'Line Chart',
    colors: [
      { label: 'Line', colorKeys: ['line'], canSelectAlpha: true },
      { label: 'Area fill', colorKeys: ['background'], canSelectAlpha: true },
    ],
  },
  {
    title: 'General',
    colors: [
      { label: 'Background', colorKeys: ['canvasFill'], canSelectAlpha: false },
      { label: 'Grid line', colorKeys: ['grid'], canSelectAlpha: true },
      { label: 'Pre-market Overlay', colorKeys: ['premarketFade'], canSelectAlpha: true },
      { label: 'After-market Overlay', colorKeys: ['aftermarketFade'], canSelectAlpha: true },
    ],
  },
]
const customColorKeys = editableColorGroups.flatMap(({ colors }) => colors.flatMap(({ colorKeys }) => colorKeys))

function getCustomColors(chartLayoutModel: ChartLayoutModel) {
  const settings = getChartLayoutSettings(chartLayoutModel)
  const layoutModelColors = settings.ChartSettings.general.Colors

  return {
    [Theme.light]: Object.entries(layoutModelColors.light!).reduce<Partial<IChartThemeColors>>(
      (acc, [key, value]) =>
        customColorKeys.includes(key as keyof IChartThemeColors) ? { ...acc, [key]: value } : acc,
      {}
    ),
    [Theme.dark]: Object.entries(layoutModelColors.dark!).reduce<Partial<IChartThemeColors>>(
      (acc, [key, value]) =>
        customColorKeys.includes(key as keyof IChartThemeColors) ? { ...acc, [key]: value } : acc,
      {}
    ),
  }
}

const defaultColors = {
  [Theme.light]: Object.entries(chartLightTheme).reduce<Partial<IChartThemeColors>>(
    (acc, [key, value]) => (customColorKeys.includes(key as keyof IChartThemeColors) ? { ...acc, [key]: value } : acc),
    {}
  ),
  [Theme.dark]: Object.entries(chartDarkTheme).reduce<Partial<IChartThemeColors>>(
    (acc, [key, value]) => (customColorKeys.includes(key as keyof IChartThemeColors) ? { ...acc, [key]: value } : acc),
    {}
  ),
}

export function ColorsTab({ onClose, onChangeSettingsTab }: ChartTabComponentProps) {
  const { forceUpdate, forceUpdateKey } = useForceUpdate()
  const { isSaving, saveSettings } = useSaveSettings()
  const { chartLayoutModel, generateNewLayoutModelConfigSnapshot } = useChartLayoutGlobalModel()

  const handleColorChange = React.useCallback(
    ({ theme, colorKey, color }: { theme: Theme; colorKey: keyof IChartThemeColors; color: string }) => {
      if (chartLayoutModel) {
        chartLayoutModel.settings.ChartSettings.general.Colors![theme]![colorKey] = color
        chartLayoutModel.save()
        chartLayoutModel.trigger('theme')
      }
    },
    [chartLayoutModel]
  )

  const setDefaultColors = React.useCallback(async () => {
    if (chartLayoutModel) {
      chartLayoutModel.settings.ChartSettings.general.Colors = {
        ...chartLayoutModel.settings.ChartSettings.general.Colors,
        [Theme.light]: {
          ...chartLayoutModel.settings.ChartSettings.general.Colors[Theme.light],
          ...defaultColors[Theme.light],
        },
        [Theme.dark]: {
          ...chartLayoutModel.settings.ChartSettings.general.Colors[Theme.dark],
          ...defaultColors[Theme.dark],
        },
      }
      chartLayoutModel.save()
      chartLayoutModel.trigger('theme')
      forceUpdate()
      window.gtag?.('event', 'restore-default', { event_category: 'settings', event_label: 'colors' })
    }
  }, [chartLayoutModel, forceUpdate])

  const handleSubmit = React.useCallback(async () => {
    if (chartLayoutModel) {
      await saveSettings({ colors: getCustomColors(chartLayoutModel) })
      generateNewLayoutModelConfigSnapshot()
      onClose()
    }
  }, [chartLayoutModel, generateNewLayoutModelConfigSnapshot, onClose, saveSettings])

  if (!chartLayoutModel) {
    return null
  }
  return (
    <React.Fragment key={forceUpdateKey}>
      <DialogHeader className="landscape-secondary:pr-inset-right portrait:pt-inset-top" onCloseClick={onClose}>
        Chart colors
      </DialogHeader>
      <DialogBody className="flex-1 landscape-secondary:mr-inset-right" data-testid="chart-settings-body">
        {editableColorGroups.map(({ title, colors }) => (
          <div key={title} className="mt-3 grid gap-1">
            <div className="mb-2 grid grid-cols-3 gap-1">
              <span className="text-sm font-bold">{title}</span>
              <span className="text-sm text-gray-500">Light Mode</span>
              <span className="text-sm text-gray-500">Dark Mode</span>
            </div>
            {colors.map(({ label, colorKeys, canSelectAlpha }) => (
              <div key={label} className="grid grid-cols-3 items-center gap-1">
                <span className="text-sm">{label}</span>
                {Object.values(Theme).map((themeValue) => (
                  <div key={themeValue} className="flex">
                    {colorKeys.map((colorKey, index) => {
                      let iconKey
                      if (colorKeys.length === 2) {
                        iconKey = index === 0 ? 'trendingUp' : 'trendingDown'
                      }
                      return (
                        <InnerState
                          key={colorKey}
                          as={ButtonColorPicker}
                          buttonPickerDataTestId={`${title}-${label}-${themeValue}${
                            iconKey ? `-${iconKey}` : ''
                          }`.replace(/ |,/g, '')}
                          canSelectCustom
                          canSelectAlpha={canSelectAlpha}
                          size="medium"
                          icon={iconKey}
                          valueKey="colors"
                          colors={[
                            {
                              color: chartLayoutModel.settings.ChartSettings.general.Colors[themeValue]![colorKey],
                              label: '',
                            },
                          ]}
                          hideOnClickOutside={false}
                          onOverlayClick={false}
                          onChange={([{ color }]: Array<{ color: string }>) => {
                            handleColorChange({ color, theme: themeValue, colorKey })
                          }}
                        />
                      )
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </DialogBody>
      <DialogFooter
        padding={false}
        className="bg-gray-50 p-1 [--inset-b:0.25rem] landscape-secondary:pr-inset-right landscape-any:pb-inset-bottom dark:bg-gray-800"
      >
        <SettingsActionFooter
          resetSettingsButtonLabel="Restore defaults"
          resetConfirmationTitle="Restore defaults?"
          resetConfirmationText="Are you sure you want to reset color settings to their default values? Your custom settings will be lost."
          settingsTabButtonLabel={
            <>
              <span className="hidden xs:inline">Adjust Study Settings</span>
              <span className="inline xs:hidden">Study Settings</span>
            </>
          }
          settingsTabButtonIconName="settingsWheel"
          tabButtonTestId="chart-settings-tab-button"
          isActionPending={isSaving}
          onSettingsTabButtonClick={() => {
            onChangeSettingsTab(SettingsTab.Chart)
          }}
          onActionButtonClick={handleSubmit}
          onSettingsReset={setDefaultColors}
        />
      </DialogFooter>
    </React.Fragment>
  )
}
